import { useQuery } from "@apollo/client";
import { GET_ORGANIZATION_BY_SLUG } from "../gql";
import type { Organization, OrganizationsConnection } from "@casey/db-types";
import { useMemo } from "react";
import { deserializeJsonbFields } from "@casey/utils/jsonb";

interface GetOrganizationBySlugVariables {
  organizationSlug: string;
}

interface GetOrganizationBySlugResponse {
  organizations: OrganizationsConnection;
}

export interface UseOrganizationResult {
  organization: Organization | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch an organization by slug
 * @param organizationSlug - The organization slug to fetch
 */
export function useOrganization(
  organizationSlug?: string,
): UseOrganizationResult {
  const { data, loading, error, refetch } = useQuery<
    GetOrganizationBySlugResponse,
    GetOrganizationBySlugVariables
  >(GET_ORGANIZATION_BY_SLUG, {
    variables: { organizationSlug: organizationSlug || "" },
    skip: !organizationSlug,
    errorPolicy: "all",
  });

  const rawOrganization = data?.organizations?.nodes?.[0] || null;

  // Deserialize JSON fields (context, onboardingStatus)
  const organization = useMemo(() => {
    if (!rawOrganization) return null;

    return deserializeJsonbFields(rawOrganization, {
      context: "organization context",
      onboardingStatus: "onboarding status",
    });
  }, [rawOrganization]);

  return {
    organization,
    loading,
    error: error || null,
    refetch,
  };
}
